import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search, Smartphone, Trash2, Loader2 } from 'lucide-react'
import { simService, type SimCard } from '../services/simService'

// SimAssignModal — replaces the legacy Ionic "coming soon" toast for
// SIM assignment. Mirrors the visual flow of AttachAssetModal: search
// box, count, scrollable list, current banner if a SIM is already
// attached, double-click confirm. Single source of truth for the SIM
// row icon (#3) in BaliseManagement's inventory table.
//
// We deliberately keep filters client-side for v1 — the existing Java
// /api/bm/simcards/available endpoint returns up to 200 cards which is
// well above current data volumes. Server-side filtering can be layered
// in later without changing the modal's contract.

export interface SimAssignModalProps {
  universalId: string
  deviceLabel?: string
  // The currently-attached SIM (read off the balise row). When set, the
  // modal renders a "current SIM" banner with a Detach button. Pass null
  // when the row has no SIM yet — modal switches to pure picker mode.
  currentSim?: { iccid: string; phoneNumber?: string | null } | null
  showMessage: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  onClose: () => void
  onChanged: () => void
}

const SimAssignModal: React.FC<SimAssignModalProps> = ({
  universalId,
  deviceLabel,
  currentSim,
  showMessage,
  onClose,
  onChanged,
}) => {
  const { t } = useTranslation()
  const [sims, setSims] = useState<SimCard[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const [selected, setSelected] = useState<SimCard | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await simService.listAvailable({ limit: 200 })
      setSims(list)
    } catch (e: any) {
      showMessage(e?.message || t('sim_assign_load_failed', { defaultValue: 'Failed to load SIM cards' }), 'error')
      setSims([])
    } finally {
      setLoading(false)
    }
  }, [showMessage, t])

  useEffect(() => { load() }, [load])

  const filteredSims = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sims
    return sims.filter((s) =>
      String(s.iccid || '').toLowerCase().includes(q) ||
      String(s.phoneNumber || s.msisdn || '').toLowerCase().includes(q) ||
      String(s.operator || s.carrier || '').toLowerCase().includes(q) ||
      String(s.country || '').toLowerCase().includes(q) ||
      String(s.technology || '').toLowerCase().includes(q),
    )
  }, [sims, search])

  const handleAssign = useCallback(async (sim: SimCard) => {
    if (!sim?.iccid || busy) return
    setBusy(true)
    try {
      await simService.assignToBalise(universalId, {
        iccid: sim.iccid,
        phoneNumber: sim.phoneNumber || sim.msisdn,
      })
      showMessage(t('sim_assign_success', { defaultValue: 'SIM assigned to balise' }), 'success')
      onChanged()
      onClose()
    } catch (e: any) {
      showMessage(e?.message || t('sim_assign_failed', { defaultValue: 'Failed to assign SIM' }), 'error')
    } finally {
      setBusy(false)
    }
  }, [busy, universalId, showMessage, t, onChanged, onClose])

  const handleDetach = useCallback(async () => {
    if (busy || !currentSim) return
    setBusy(true)
    try {
      await simService.unassignFromBalise(universalId)
      showMessage(t('sim_unassign_success', { defaultValue: 'SIM detached from balise' }), 'success')
      onChanged()
      onClose()
    } catch (e: any) {
      showMessage(e?.message || t('sim_unassign_failed', { defaultValue: 'Failed to detach SIM' }), 'error')
    } finally {
      setBusy(false)
    }
  }, [busy, currentSim, universalId, showMessage, t, onChanged, onClose])

  return (
    <div className="fixed inset-0 z-[310] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col max-h-[85vh]">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
            <Smartphone className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {t('sim_assign_modal_title', { defaultValue: 'Manage SIM' })}
              {deviceLabel && <span className="ml-2 text-gray-500 font-normal">— {deviceLabel}</span>}
            </div>
            <div className="text-[11px] text-gray-500 truncate">
              {currentSim
                ? t('sim_assign_pick_replacement', { defaultValue: 'Pick a replacement SIM, or detach the current one.' })
                : t('sim_assign_pick', { defaultValue: 'Pick a SIM to assign to this balise.' })}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" title={t('close') as string}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {currentSim && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-3">
            <Smartphone className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-amber-900 font-medium truncate">
                {t('sim_current_label', { defaultValue: 'Currently attached' })}: {currentSim.iccid}
              </div>
              {currentSim.phoneNumber && (
                <div className="text-[11px] text-amber-800 truncate">{currentSim.phoneNumber}</div>
              )}
            </div>
            <button
              type="button"
              onClick={handleDetach}
              disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" />
              {t('sim_detach', { defaultValue: 'Detach' })}
            </button>
          </div>
        )}

        <div className="px-4 pt-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('sim_assign_search_placeholder', { defaultValue: 'Search by ICCID, phone, operator, country…' }) as string}
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            {t('sim_assign_count', { defaultValue: '{{n}} SIM cards', n: filteredSims.length })}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}
            </div>
          ) : filteredSims.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500 italic">
              {sims.length === 0
                ? t('sim_assign_none', { defaultValue: 'No available SIM cards.' })
                : t('sim_assign_no_match', { defaultValue: 'No SIM matches your search.' })}
            </div>
          ) : (
            <ul className="space-y-1">
              {filteredSims.map((s) => {
                const isCurrent = !!currentSim && currentSim.iccid === s.iccid
                const isSelected = !!selected && selected.iccid === s.iccid
                return (
                  <li
                    key={s.iccid + (s.id != null ? `-${s.id}` : '')}
                    onClick={() => setSelected(s)}
                    onDoubleClick={() => !isCurrent && handleAssign(s)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      isCurrent
                        ? 'border-amber-300 bg-amber-50'
                        : isSelected
                          ? 'border-amber-400 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/40'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Smartphone className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate font-mono">
                          {s.iccid}
                          {isCurrent && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase bg-amber-200 text-amber-900 font-semibold">
                              {t('sim_current_badge', { defaultValue: 'Current' })}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {[s.phoneNumber || s.msisdn, s.operator || s.carrier, s.country].filter(Boolean).join(' · ') || '—'}
                        </div>
                        {(s.technology || s.plan) && (
                          <div className="text-[11px] text-gray-500 truncate">
                            {[s.technology, s.plan].filter(Boolean).join(' · ')}
                            {typeof s.dataRemaining === 'number' && typeof s.dataLimit === 'number' && (
                              <span className="ml-2">
                                {t('sim_data_left', { defaultValue: '{{used}}/{{limit}} MB', used: s.dataRemaining, limit: s.dataLimit })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => selected && handleAssign(selected)}
            disabled={busy || !selected || (currentSim?.iccid === selected?.iccid)}
            className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busy
              ? t('balise_action_in_progress')
              : currentSim
                ? t('sim_replace', { defaultValue: 'Replace SIM' })
                : t('sim_assign', { defaultValue: 'Assign SIM' })}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SimAssignModal
